import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search, Loader2, MessageCircle } from "lucide-react";

type AdminUser = {
  id: string;
  email: string | null;
  displayName: string;
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  sellerType: string;
  profileImageUrl: string | null;
  isFoundingMaker: boolean;
  glutenFreeMaker: boolean;
  glutenFreeAllProducts: boolean;
  createdAt: string | null;
  listingCount: number;
};

function glutenFreeMode(u: AdminUser): "off" | "all" | "per_item" {
  if (!u.glutenFreeMaker) return "off";
  return u.glutenFreeAllProducts ? "all" : "per_item";
}

function nameOf(u: AdminUser): string {
  const full = `${u.firstName} ${u.lastName}`.trim();
  return u.displayName.trim() || full || u.email || "Unknown";
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function joinedLabel(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function joinedMs(u: AdminUser): number {
  return u.createdAt ? new Date(u.createdAt).getTime() : 0;
}

const FILTERS: { key: string; label: string; test: (u: AdminUser) => boolean }[] = [
  { key: "all", label: "All", test: () => true },
  { key: "no_listings", label: "No listings", test: (u) => u.listingCount === 0 },
  { key: "buyers", label: "Buyers", test: (u) => !u.sellerType },
  { key: "maker", label: "Makers", test: (u) => u.sellerType === "maker" },
  { key: "grower", label: "Growers", test: (u) => u.sellerType === "grower" },
  { key: "farmstand", label: "Farmstands", test: (u) => u.sellerType === "farmstand" },
  { key: "mixed", label: "Mixed", test: (u) => u.sellerType === "mixed" },
  { key: "founding", label: "Founding", test: (u) => u.isFoundingMaker },
];

const SORTS: { key: string; label: string; cmp: (a: AdminUser, b: AdminUser) => number }[] = [
  { key: "newest", label: "Newest", cmp: (a, b) => joinedMs(b) - joinedMs(a) },
  { key: "oldest", label: "Oldest", cmp: (a, b) => joinedMs(a) - joinedMs(b) },
  { key: "most", label: "Most listings", cmp: (a, b) => b.listingCount - a.listingCount },
  { key: "fewest", label: "Fewest listings", cmp: (a, b) => a.listingCount - b.listingCount },
  { key: "name", label: "Name A–Z", cmp: (a, b) => nameOf(a).localeCompare(nameOf(b)) },
];

export default function AdminUsers() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const queryClient = useQueryClient();

  const setGlutenFree = async (userId: string, mode: "off" | "all" | "per_item") => {
    const res = await fetch(`/api/admin/users/${userId}/gluten-free`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode }),
    });
    if (res.ok) queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
  };

  const { data: users = [], isLoading } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/users"],
    queryFn: async () => {
      const res = await fetch("/api/admin/users", { credentials: "include" });
      if (!res.ok) throw new Error(`admin users ${res.status}`);
      return res.json();
    },
    // Global staleTime is Infinity (trap #1) — always refetch so the list
    // reflects new signups instead of a stale cached snapshot.
    staleTime: 0,
    refetchOnMount: "always",
  });

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const f of FILTERS) out[f.key] = users.filter(f.test).length;
    return out;
  }, [users]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const f = FILTERS.find((x) => x.key === filter) ?? FILTERS[0];
    const s = SORTS.find((x) => x.key === sort) ?? SORTS[0];
    return users
      .filter(f.test)
      .filter((u) => {
        if (!q) return true;
        const hay = `${nameOf(u)} ${u.email ?? ""} ${u.city} ${u.state} ${u.sellerType}`.toLowerCase();
        return hay.includes(q);
      })
      .sort(s.cmp);
  }, [users, query, filter, sort]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="container mx-auto flex-1 px-4 pb-12 pt-3">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Users</h1>
            <p className="text-xs text-muted-foreground">
              {isLoading ? "Loading…" : `${users.length} total`}
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link href="/admin/dashboard">Dashboard</Link>
          </Button>
        </div>

        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, location…"
            className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-primary"
          />
        </div>

        <div className="-mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1">
          {FILTERS.filter((f) => f.key === "all" || f.key === filter || counts[f.key] > 0).map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`flex-shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-muted-foreground hover:border-primary/40"
                }`}
              >
                {f.label}{" "}
                <span className={active ? "opacity-80" : "opacity-60"}>{counts[f.key]}</span>
              </button>
            );
          })}
        </div>

        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {visible.length} {visible.length === 1 ? "result" : "results"}
          </span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-lg border border-border bg-background px-2 py-1 text-xs text-foreground outline-none focus:border-primary"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                Sort: {s.label}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading users…
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No users match.</div>
        ) : (
          <div className="space-y-2">
            {visible.map((u) => {
              const name = nameOf(u);
              const loc = [u.city, u.state].filter(Boolean).join(", ");
              return (
                <Card key={u.id}>
                  <CardContent className="flex items-center gap-3 p-3">
                    <Link href={`/u/${u.id}`} className="flex-shrink-0">
                      {u.profileImageUrl ? (
                        <img
                          src={u.profileImageUrl}
                          alt=""
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                          {initials(name)}
                        </div>
                      )}
                    </Link>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Link
                          href={`/u/${u.id}`}
                          className="truncate font-semibold text-foreground hover:underline"
                        >
                          {name}
                        </Link>
                        {u.isFoundingMaker && (
                          <span className="flex-shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600">
                            Founding
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {u.email || "no email"}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                        {u.sellerType && <span className="capitalize">{u.sellerType}</span>}
                        {loc && <span>· {loc}</span>}
                        {u.createdAt && <span>· joined {joinedLabel(u.createdAt)}</span>}
                        <span
                          className={
                            u.listingCount === 0 ? "font-medium text-destructive" : "text-foreground"
                          }
                        >
                          · {u.listingCount} {u.listingCount === 1 ? "listing" : "listings"}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
                      <Button asChild size="sm" variant="outline">
                        <Link href={`/admin/messages?user=${u.id}&name=${encodeURIComponent(name)}`}>
                          <MessageCircle className="mr-1 h-3.5 w-3.5" />
                          Message
                        </Link>
                      </Button>
                      <select
                        value={glutenFreeMode(u)}
                        onChange={(e) => setGlutenFree(u.id, e.target.value as "off" | "all" | "per_item")}
                        className="rounded-md border border-input bg-background px-1.5 py-1 text-[11px]"
                        title="Gluten-free mode"
                      >
                        <option value="off">GF: off</option>
                        <option value="all">GF: all products</option>
                        <option value="per_item">GF: per product</option>
                      </select>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
